// Piezas de presentación de las pestañas de análisis de Costos & BI
// (Dashboard · Ventas · Ranking). Solo dibujan: todos los números llegan calculados
// desde Costs.tsx, así que acá no hay cuentas de negocio.
import React from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { AnimatedCounter } from '../ui/arc/animated-counter';
import SegmentedControl from '../ui/arc/segmented-control';
import { cn } from '../ui/Table';
import mc from '../ui/arc/metric-card.module.css';
import an from './analisis.module.css';

// ── Formato ──────────────────────────────────────────────────────────────────
/** Pesos sin centavos ($ 1.234.567): en un tablero los centavos son ruido. */
export const ars = (n: number) => '$' + Math.round(n || 0).toLocaleString('es-AR');
/** Porcentaje con coma decimal (es-AR). */
export const pct = (n: number, d = 1) => (Number.isFinite(n) ? n : 0).toLocaleString('es-AR', { minimumFractionDigits: d, maximumFractionDigits: d }) + '%';
/** Eje de los gráficos: $2,5 M / $850 k. */
export const arsCompact = (v: number) => {
    const a = Math.abs(v);
    if (a >= 1e6) return '$' + (v / 1e6).toLocaleString('es-AR', { maximumFractionDigits: 1 }) + ' M';
    if (a >= 1e3) return '$' + Math.round(v / 1e3).toLocaleString('es-AR') + ' k';
    return '$' + Math.round(v).toLocaleString('es-AR');
};

/** Qué parte de la facturación se llevan los N productos que más facturan (0–100). */
export const topShare = (rows: { revenue: number }[], total: number, n: number) =>
    total > 0 ? ([...rows].sort((a, b) => b.revenue - a.revenue).slice(0, n).reduce((s, r) => s + r.revenue, 0) / total) * 100 : 0;

// ── Paleta de gráficos (skill dataviz, tema claro) ───────────────────────────
// Categórica en orden fijo (nunca se cicla) + tinta y grilla recesivas.
export const VIZ = {
    s1: '#2a78d6', // azul
    s2: '#eb6834', // naranja
    grid: '#e1e0d9',
    axis: '#c3c2b7',
    muted: '#898781',
    ink2: '#52514e',
};

// ── Encabezado de pestaña ────────────────────────────────────────────────────
export function TabHeader({ title, purpose, actions }: { title: string; purpose: React.ReactNode; actions?: React.ReactNode }) {
    return (
        <header className="mb-5 flex flex-wrap items-end justify-between gap-4">
            <div className="min-w-0">
                <h1 className="text-[length:var(--text-2xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">{title}</h1>
                <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">{purpose}</p>
            </div>
            {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
    );
}

// ── Filtro de período (mismo control en Ventas y Ranking) ────────────────────
export type RangeKey = 'today' | '7d' | '14d' | 'month' | '30d' | 'all';
export const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
    { value: 'today', label: 'Hoy' },
    { value: '7d', label: '7 días' },
    { value: '14d', label: '14 días' },
    { value: 'month', label: 'Mes actual' },
    { value: '30d', label: '30 días' },
    { value: 'all', label: 'Todo' },
];
export const rangeLabel = (k: string) => RANGE_OPTIONS.find(r => r.value === k)?.label || '';

/** Barra de filtros: período + (opcional) mes puntual y controles extra. Una sola fila, envuelve en mobile. */
export function FilterBar({ range, onRange, month, months, onMonth, children, summary }: {
    range: RangeKey | null;
    onRange: (r: RangeKey) => void;
    month?: string;
    months?: [string, string][];
    onMonth?: (m: string) => void;
    children?: React.ReactNode;
    summary?: React.ReactNode;
}) {
    return (
        <div className="mb-5 flex flex-wrap items-center gap-2">
            {/* value '' = ningún rango marcado (cuando se eligió un mes puntual) */}
            <SegmentedControl label="Período" value={range || ''} onValueChange={v => onRange(v as RangeKey)} options={RANGE_OPTIONS} />
            {months && onMonth && (
                <select
                    aria-label="Mes"
                    value={month}
                    onChange={e => onMonth(e.target.value)}
                    className={cn(
                        'h-[var(--control-height-md)] min-w-0 max-w-full rounded-[var(--radius-control)] border border-line bg-card px-3 text-[length:var(--text-sm)] text-ink',
                        month !== 'all' && 'border-[var(--border-strong)] font-medium',
                    )}
                >
                    <option value="all">Elegir un mes…</option>
                    {months.map(([key, label]) => (
                        <option key={key} value={key}>{label.charAt(0).toUpperCase() + label.slice(1)}</option>
                    ))}
                </select>
            )}
            {children}
            {summary && <span className="ml-auto text-[length:var(--text-sm)] text-faint tabular-nums">{summary}</span>}
        </div>
    );
}

// ── KPI ──────────────────────────────────────────────────────────────────────
// Mismo look que MetricCard de uiarc (usa su CSS), pero acepta prefijo ($) y decimales,
// que la MetricCard original no expone. La variación vs el período anterior va en el chip.
export interface KpiChange { pct: number; title: string; /** true = subir es malo (pendiente, descuentos). */ invert?: boolean }
export function Kpi({ label, value, prefix, suffix, decimals = 0, context, change, title, className }: {
    label: string; value: number; prefix?: string; suffix?: string; decimals?: number;
    context: React.ReactNode; change?: KpiChange | null; title?: string; className?: string;
}) {
    let chip: React.ReactNode = null;
    if (change) {
        const zero = Math.abs(change.pct) < 0.1;
        const up = change.pct > 0;
        const good = change.invert ? !up : up;
        chip = (
            <small data-trend={zero ? undefined : good ? 'up' : 'down'} title={change.title}>
                <span className="inline-flex items-center gap-0.5">
                    {zero ? <Minus size={12} /> : up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {pct(Math.abs(change.pct))}
                </span>
            </small>
        );
    }
    return (
        <article className={cn(mc.card, className)} title={title}>
            <div className={mc.top}><span>{label}</span>{chip}</div>
            <div className={an.fit}><AnimatedCounter value={value} prefix={prefix} suffix={suffix} decimals={decimals} locale="es-AR" /></div>
            <p>{context}</p>
        </article>
    );
}

// ── Panel ────────────────────────────────────────────────────────────────────
export function Panel({ title, icon, subtitle, actions, children, className, bodyClassName }: {
    title: React.ReactNode; icon?: React.ReactNode; subtitle?: React.ReactNode; actions?: React.ReactNode;
    children: React.ReactNode; className?: string; bodyClassName?: string;
}) {
    return (
        <section className={cn('min-w-0 rounded-[var(--radius-surface)] border border-line bg-card shadow-xs', className)}>
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3">
                <div className="min-w-0">
                    <h2 className="flex items-center gap-2 text-[length:var(--text-base)] font-semibold text-ink">
                        {icon && <span className="text-faint">{icon}</span>}{title}
                    </h2>
                    {subtitle && <p className="mt-0.5 text-[length:var(--text-sm)] text-faint">{subtitle}</p>}
                </div>
                {actions}
            </div>
            <div className={cn('p-4', bodyClassName)}>{children}</div>
        </section>
    );
}

// ── Lista de barras (parte de un todo, ordenada) ─────────────────────────────
// Reemplaza a las tortas: con 5+ porciones una torta no se puede comparar a ojo,
// una barra horizontal sí. Una sola serie → un solo color; la identidad la da el texto.
export function BarList({ items, empty }: {
    items: { key: string; label: string; share: number; value: string; sub?: string }[];
    empty?: string;
}) {
    if (!items.length) return <p className="py-6 text-center text-[length:var(--text-sm)] text-faint">{empty || 'Sin datos en el período.'}</p>;
    const max = Math.max(...items.map(i => i.share), 0.0001);
    return (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {items.map(i => (
                <li key={i.key} className="min-w-0">
                    <div className="mb-1 flex items-baseline justify-between gap-3 text-[length:var(--text-sm)]">
                        <span className="min-w-0 truncate font-medium text-ink">{i.label}</span>
                        <span className="shrink-0 tabular-nums text-ink">
                            {i.value}
                            <span className="ml-2 inline-block min-w-[3.5rem] text-right text-faint">{pct(i.share)}</span>
                        </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-[#f0efec]" aria-hidden="true">
                        <div className="h-full rounded-full" style={{ width: `${Math.max((i.share / max) * 100, 1.5)}%`, background: VIZ.s1 }} />
                    </div>
                    {i.sub && <div className="mt-0.5 text-[length:var(--text-xs)] text-faint">{i.sub}</div>}
                </li>
            ))}
        </ul>
    );
}

// ── Tooltip de recharts con el estilo del admin ──────────────────────────────
export function ChartTooltip({ active, payload, label, rows }: {
    active?: boolean; payload?: any[]; label?: string;
    rows: (p: any) => { label: string; value: string; color?: string; strong?: boolean }[];
}) {
    if (!active || !payload?.length) return null;
    const data = payload[0]?.payload;
    return (
        <div className="tw min-w-[11rem] rounded-[var(--radius-control)] border border-line bg-card px-3 py-2 text-[length:var(--text-sm)] shadow-[var(--shadow-floating)]">
            <div className="mb-1 font-semibold text-ink">{label}</div>
            {rows(data).map(r => (
                <div key={r.label} className="flex items-center justify-between gap-4 tabular-nums">
                    <span className="flex items-center gap-1.5 text-soft">
                        {r.color && <span className="inline-block h-2 w-2 rounded-full" style={{ background: r.color }} />}{r.label}
                    </span>
                    <span className={cn('text-ink', r.strong && 'font-semibold')}>{r.value}</span>
                </div>
            ))}
        </div>
    );
}

export function LegendDot({ color, label }: { color: string; label: string }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-[length:var(--text-sm)] text-soft">
            <span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ background: color }} />{label}
        </span>
    );
}
